import React, { Fragment } from 'react';

import ActualVsBudgetReportHeader from './ActualVsBudgetReportHeader';
import ActualVsBudgetReportLegend from './ActualVsBudgetReportLegend';
import ActualVsBudgetReportSummaryTableRow from './ActualVsBudgetReportSummaryTableRow';
import { useOptionalActualVsBudgetReportContext } from './context/ActualVsBudgetReportContext';
import { buildGroupedHeaderRows } from './helpers/reportHeaderGroups';
import { isSummaryRowEmpty } from './helpers/reportRowVisibility';
import { resolveVersionNames } from './helpers/reportVersionNames';
import { resolveVisibleColumns } from './helpers/reportVisibleColumns';
import { ReportColumnHeaderRow, ReportGroupHeaderRow } from './ReportTableHeaderRows';
import { TYPE_EVENT } from './types/ModuleType';
import { SUMMARY_SUPPLEMENTARY_COLUMNS } from './types/ReportColumns';
import { NOT_IN_CURRENT_STRUCTURE_ID } from './types/ReportType';

// Time-base data columns (Month / Reporting Period / Lifetime, each with
// Budget / Actuals / Variance) are always rendered.
const TIME_BASE_COLUMN_COUNT = 9;

/**
 * Split a section's id list into total rows and (empty-filtered) regular rows.
 * A department group is empty when its aggregate row has no displayed value —
 * because cost-centre sub-rows roll up into it, an empty department implies
 * empty children.
 */
const partitionSection = (ids, dataMap, hideEmptyRows, visibleColumns) => {
    const totals = [];
    const regular = [];

    (ids || []).forEach((id) => {
        if (id === 'total') {
            totals.push(id);
            return;
        }

        // The synthetic "Not in current structure" row carries only comparison
        // figures (its current-structure figures are all nil), so the empty-row
        // filter must never suppress it.
        if (id === NOT_IN_CURRENT_STRUCTURE_ID) {
            regular.push(id);
            return;
        }

        if (hideEmptyRows && isSummaryRowEmpty(dataMap[id], visibleColumns)) {
            return;
        }

        regular.push(id);
    });

    return { totals, regular, isEmpty: regular.length === 0 };
};

/**
 * A single department row plus, at Department + Cost Centre level, its
 * cost-centre breakdown rendered as indented sub-rows. Empty sub-rows are
 * suppressed when "Hide rows with no values" is on.
 */
const SummaryDepartmentGroup = ({
    keyPrefix,
    id,
    data,
    isTotalRow,
    visibleColumns,
    hideEmptyRows,
    comparisonActive,
    comparisonName,
    favourableWhenNegative,
}) => {
    if (!data) {
        return null;
    }

    // Comparison-only lines grouped by the backend — rendered muted, never with
    // a cost-centre breakdown.
    const isUnmatched = id === NOT_IN_CURRENT_STRUCTURE_ID;

    const costcentres =
        data.costcentres && !isTotalRow && !isUnmatched
            ? Object.entries(data.costcentres).filter(([, ccData]) => !hideEmptyRows || !isSummaryRowEmpty(ccData, visibleColumns))
            : [];

    return (
        <Fragment>
            <ActualVsBudgetReportSummaryTableRow
                // Data rows are white; the unmatched row is set apart by its muted
                // italic label rather than a fill, so it needs no override.
                className={undefined}
                key={`${keyPrefix}-${id}`}
                isTotalRow={isTotalRow}
                data={data}
                visibleColumns={visibleColumns}
                comparisonActive={comparisonActive}
                comparisonName={comparisonName}
                favourableWhenNegative={favourableWhenNegative}
            />
            {costcentres.map(([ccId, ccData]) => (
                <ActualVsBudgetReportSummaryTableRow
                    key={`${keyPrefix}-${id}-cc-${ccId}`}
                    isSubRow
                    data={ccData}
                    visibleColumns={visibleColumns}
                    comparisonActive={comparisonActive}
                    comparisonName={comparisonName}
                    favourableWhenNegative={favourableWhenNegative}
                />
            ))}
        </Fragment>
    );
};

const ActualVsBudgetReportSummaryTable = ({ summary: propSummary, module: propModule, Actions, storedReport = null }) => {
    const context = useOptionalActualVsBudgetReportContext();
    const module = propModule || context.module;
    const summary = propSummary || context.summary;
    // A generated report renders outside the provider, so its column set comes
    // from the report itself rather than from the live configuration screen.
    const visibleColumns = resolveVisibleColumns(context, storedReport);
    const hideEmptyRows = context.hideEmptyRows ?? false;

    // Comparison is engaged whenever the backend attached its top-level marker
    // (only ever set at the Department + Cost Centre level). When absent the
    // report renders exactly as it does today.
    const comparisonActive = !!summary && summary.unmatchedComparisonCount !== undefined && summary.unmatchedComparisonCount !== null;
    const unmatchedCount = comparisonActive ? summary.unmatchedComparisonCount : 0;
    // The report's own version and the comparison version, for the AB column
    // labels, the banner and the legend. Both fall back to generic labels when
    // rendered outside the provider (e.g. an archived report).
    const { versionName, comparisonName } = resolveVersionNames(context, storedReport);

    const visibleSupplementary = SUMMARY_SUPPLEMENTARY_COLUMNS.filter((key) => visibleColumns[key]);
    // Data columns after the label — used for the section-header / empty-state
    // spans. Comparison adds two lifetime columns (AB Budget + Var v AB).
    const dataColumnCount = TIME_BASE_COLUMN_COUNT + (comparisonActive ? 2 : 0) + visibleSupplementary.length;

    const headerRows = buildGroupedHeaderRows({
        supplementaryKeys: SUMMARY_SUPPLEMENTARY_COLUMNS,
        visibleColumns,
        months: summary?.months || [],
        rowLabel: $t('Department / Cost Centre'),
        comparisonActive,
        comparisonName,
    });

    const renderSection = (label, sectionKey, favourableWhenNegative) => {
        const { totals, regular, isEmpty } = partitionSection(summary.departments[sectionKey], summary[sectionKey], hideEmptyRows, visibleColumns);

        return (
            <>
                {/* Section band: one charcoal strip naming the side of the
                    report, spanning every column so it reads as a divider. */}
                <tr className="avb-report-table__section tw-text-[12.5px] tw-font-medium !tw-bg-charcoal-60 tw-text-white">
                    <td colSpan={dataColumnCount + 1}>{label}</td>
                </tr>
                {isEmpty ? (
                    <tr>
                        <td colSpan={dataColumnCount + 1} className="tw-text-xs tw-italic tw-text-charcoal-50">
                            {$t('No rows with values')}
                        </td>
                    </tr>
                ) : (
                    <>
                        {regular.map((id) => (
                            <SummaryDepartmentGroup
                                key={`${sectionKey}-${id}`}
                                keyPrefix={sectionKey}
                                id={id}
                                isTotalRow={false}
                                data={summary[sectionKey][id]}
                                visibleColumns={visibleColumns}
                                hideEmptyRows={hideEmptyRows}
                                comparisonActive={comparisonActive}
                                comparisonName={comparisonName}
                                favourableWhenNegative={favourableWhenNegative}
                            />
                        ))}
                        {totals.map((id) => (
                            <SummaryDepartmentGroup
                                key={`${sectionKey}-${id}`}
                                keyPrefix={sectionKey}
                                id={id}
                                isTotalRow
                                data={summary[sectionKey][id]}
                                visibleColumns={visibleColumns}
                                hideEmptyRows={hideEmptyRows}
                                comparisonActive={comparisonActive}
                                comparisonName={comparisonName}
                                favourableWhenNegative={favourableWhenNegative}
                            />
                        ))}
                    </>
                )}
            </>
        );
    };

    return (
        <div>
            <ActualVsBudgetReportHeader unmatchedCount={comparisonActive ? unmatchedCount : 0} comparisonName={comparisonName} storedReport={storedReport} />
            <div className="tw-mt-4 tw-flex tw-gap-x-4 tw-items-end tw-mb-4">
                <div className="tw-ms-auto">{Actions}</div>
            </div>
            <div className="inbud-table_wrapper avb-report-wrapper">
                <table className="inbud-table avb-report-table inbud-table_first-sticky">
                    <thead className="tw-sticky tw-top-0 tw-z-10">
                        <ReportGroupHeaderRow groups={headerRows.groups} />
                        <ReportColumnHeaderRow columns={headerRows.columns} />
                    </thead>
                    {!!summary && !!summary.departments && (
                        <tbody className="tw-align-top">
                            {module === TYPE_EVENT && renderSection($t('Revenue'), 'revenue', true)}

                            {module === TYPE_EVENT ? (
                                renderSection($t('Expenditure'), 'expenditure', false)
                            ) : (
                                <>
                                    {(() => {
                                        const { regular, totals } = partitionSection(
                                            summary.departments.expenditure,
                                            summary.expenditure,
                                            hideEmptyRows,
                                            visibleColumns,
                                        );

                                        return [...regular, ...totals].map((depId) => (
                                            <SummaryDepartmentGroup
                                                key={`expenditure-${depId}`}
                                                keyPrefix="expenditure"
                                                id={depId}
                                                isTotalRow={depId === 'total'}
                                                data={summary.expenditure[depId]}
                                                visibleColumns={visibleColumns}
                                                hideEmptyRows={hideEmptyRows}
                                                comparisonActive={comparisonActive}
                                                comparisonName={comparisonName}
                                                favourableWhenNegative={false}
                                            />
                                        ));
                                    })()}
                                </>
                            )}

                            {module === TYPE_EVENT &&
                                summary.departments.net.map((revId) => {
                                    const data = summary.net[revId];

                                    return (
                                        <ActualVsBudgetReportSummaryTableRow
                                            className="tw-text-[12.5px] !tw-bg-charcoal !tw-text-white avb-report-table__total"
                                            key={`net-${revId}`}
                                            data={data}
                                            isTotalRow
                                            visibleColumns={visibleColumns}
                                            comparisonActive={comparisonActive}
                                            comparisonName={comparisonName}
                                            favourableWhenNegative
                                        />
                                    );
                                })}
                        </tbody>
                    )}
                </table>
            </div>
            <ActualVsBudgetReportLegend versionName={versionName} comparisonName={comparisonActive ? comparisonName : ''} />
        </div>
    );
};

export default ActualVsBudgetReportSummaryTable;
